import { authService } from './auth';
import { config } from '../config';

export interface ArtGenerationRequest {
  prompt: string;
  scriptId: string; // Added required scriptId
  resolution?: 'square' | 'landscape' | 'portrait';
  quality?: 'fast' | 'high';
  seed?: number;
}

export interface ArtGenerationResponse {
  success?: boolean;
  art?: {
    s3Key?: string;  // New format: just the filename
    s3Url?: string;  // Full S3 URL
    full?: string;   // Old format
    half?: string;   // Old format
  };
  // Legacy fields for backward compatibility
  id?: string;
  status?: 'starting' | 'processing' | 'succeeded' | 'failed' | 'canceled';
  output?: string; // Single image URL from Replicate
  error?: string;
  urls?: {
    get: string;
    cancel: string;
    stream?: string;
    web?: string;
  };
  created_at?: string;
  completed_at?: string;
  started_at?: string;
  input?: any;
  logs?: string;
  metrics?: {
    predict_time?: number;
    total_time?: number;
  };
  // Our local fields
  local_file_path?: string; // Path to downloaded file
  local_url?: string; // URL to serve the local file
}

class ArtService {
  private apiUrl = config.apiUrl;

  private getAuthHeaders() {
    const token = authService.getToken();
    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    };
  }

  /**
   * Generate art for meditation scripts
   */
  async generateArt(request: ArtGenerationRequest): Promise<ArtGenerationResponse> {
    try {
      const response = await fetch(`${this.apiUrl}/generate-art`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(request)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Generate art error:', error);
      throw error;
    }
  }

  /**
   * Get the status of an art generation job
   */
  async getArtStatus(predictionId: string): Promise<ArtGenerationResponse> {
    try {
      const response = await fetch(`${this.apiUrl}/art-status/${predictionId}`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Get art status error:', error);
      throw error;
    }
  }

  /**
   * Cancel an art generation job
   */
  async cancelArtGeneration(predictionId: string): Promise<ArtGenerationResponse> {
    try {
      const response = await fetch(`${this.apiUrl}/cancel-art/${predictionId}`, {
        method: 'POST',
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Cancel art generation error:', error);
      throw error;
    }
  }

  /**
   * Poll for art generation completion
   */
  async pollForCompletion(
    predictionId: string, 
    onProgress?: (status: ArtGenerationResponse) => void,
    maxAttempts: number = 60,
    intervalMs: number = 2000
  ): Promise<ArtGenerationResponse> {
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        const status = await this.getArtStatus(predictionId);
        
        if (onProgress) {
          onProgress(status);
        }

        if (status.status === 'succeeded' || status.status === 'failed' || status.status === 'canceled') {
          return status;
        }

        // Wait before next poll
        await new Promise(resolve => setTimeout(resolve, intervalMs));
      } catch (error) {
        console.error(`Polling attempt ${attempt + 1} failed:`, error);
        
        // If this is the last attempt, throw the error
        if (attempt === maxAttempts - 1) {
          throw error;
        }
        
        // Otherwise, wait and try again
        await new Promise(resolve => setTimeout(resolve, intervalMs));
      }
    }

    throw new Error('Art generation polling timed out');
  }

  /**
   * Generate art and wait for completion
   */
  async generateArtAndWait(
    request: ArtGenerationRequest,
    onProgress?: (status: ArtGenerationResponse) => void
  ): Promise<ArtGenerationResponse> {
    const initialResponse = await this.generateArt(request);
    
    // Check if this is the new format response (immediate completion)
    if (initialResponse.success && initialResponse.art) {
      // Art generation completed immediately
      if (onProgress) {
        onProgress({
          ...initialResponse,
          status: 'succeeded'
        });
      }
      return initialResponse;
    }
    
    // Legacy polling format
    if (initialResponse.status === 'succeeded') {
      return initialResponse;
    }

    if (initialResponse.status === 'failed') {
      throw new Error(initialResponse.error || 'Art generation failed');
    }

    // Poll for completion (legacy)
    return this.pollForCompletion(initialResponse.id!, onProgress);
  }
}

export const artService = new ArtService();